import { useEffect, useRef, useState, type DragEvent } from "react";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Loader2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  uploadApi,
  type UploadImageResponse,
  type UploadModule,
} from "@/lib/api/services/upload.api";

const MAX_IMAGE_DIMENSION = 2000;
const DEFAULT_MAX_FILES = 5;
const DEFAULT_MAX_TOTAL_SIZE_MB = 10;
const SUPPORTED_IMAGE_TYPES = "SVG, PNG, JPG, GIF, WEBP";

type DetectedFormat = "jpeg" | "png" | "gif" | "webp" | "svg";

export interface UploadedFileItem {
  id: string;
  fileName: string;
  key: string;
  imageUrl: string;
  thumbnailKey: string;
  thumbnailUrl: string;
  fileHash: string;
  sizeBytes: number;
  width: number;
  height: number;
  previewUrl: string;
}

export interface ExistingImagePreview {
  imageUrl?: string;
  thumbnailUrl?: string;
  label?: string;
}

interface ProcessedImage {
  file: File;
  format: DetectedFormat;
  originalName: string;
  originalSizeBytes: number;
  originalWidth: number;
  originalHeight: number;
  processedWidth: number;
  processedHeight: number;
  previewUrl: string;
}

interface FileUploaderProps {
  module: UploadModule;
  label?: string;
  optional?: boolean;
  accept?: string;
  hint?: string;
  maxFiles?: number;
  maxTotalSizeMb?: number;
  disabled?: boolean;
  multiple?: boolean;
  className?: string;
  onUploadSuccess?: (results: UploadImageResponse[]) => void;
  onUploadedItemsChange?: (items: UploadedFileItem[]) => void;
  onUploadError?: (error: Error) => void;
  existingImage?: ExistingImagePreview;
  onExistingImageDetachedChange?: (detached: boolean) => void;
}

function createUuid32(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "");
  }
  const alphabet = "abcdef0123456789";
  return Array.from({ length: 32 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join("");
}

async function hashFile(file: Blob): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function extensionForFormat(format: DetectedFormat): string {
  switch (format) {
    case "jpeg":
      return ".jpg";
    case "png":
      return ".png";
    case "gif":
      return ".gif";
    case "webp":
      return ".webp";
    case "svg":
      return ".svg";
  }
}

function mimeForFormat(format: DetectedFormat): string {
  switch (format) {
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "svg":
      return "image/svg+xml";
  }
}

function getFileNameFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const raw = pathname.split("/").pop() || "";
    return decodeURIComponent(raw) || "image";
  } catch {
    return "image";
  }
}

function detectImageFormat(bytes: Uint8Array): DetectedFormat | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "jpeg";
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return "png";
  }
  if (bytes.length >= 4 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
    return "gif";
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "webp";
  }

  const text = new TextDecoder("utf-8").decode(bytes.slice(0, 512)).trim().toLowerCase();
  if (text.startsWith("<svg") || text.includes("<svg")) {
    return "svg";
  }

  return null;
}

async function decodeDimensions(blob: Blob): Promise<{ width: number; height: number }> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(blob);
    const dimensions = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return dimensions;
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      reject(new Error(t`Invalid image data`));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

async function resizeRasterImage(file: Blob, mimeType: string, width: number, height: number): Promise<Blob> {
  const scale = Math.min(MAX_IMAGE_DIMENSION / width, MAX_IMAGE_DIMENSION / height);
  const nextWidth = Math.max(1, Math.round(width * scale));
  const nextHeight = Math.max(1, Math.round(height * scale));

  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement("canvas");
  canvas.width = nextWidth;
  canvas.height = nextHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    throw new Error(t`Failed to create canvas context for image resizing`);
  }

  ctx.drawImage(bitmap, 0, 0, nextWidth, nextHeight);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((out) => resolve(out), mimeType, 0.92);
  });

  if (!blob) {
    throw new Error(t`Failed to resize image`);
  }

  return blob;
}

async function processImageFile(rawFile: File): Promise<ProcessedImage> {
  const buffer = await rawFile.arrayBuffer();
  const detectedFormat = detectImageFormat(new Uint8Array(buffer));

  if (!detectedFormat) {
    throw new Error(t`File content is not a supported image format`);
  }

  const detectedMime = mimeForFormat(detectedFormat);
  const sourceBlob = new Blob([buffer], { type: detectedMime });
  const { width: originalWidth, height: originalHeight } = await decodeDimensions(sourceBlob);

  const needsResize = originalWidth > MAX_IMAGE_DIMENSION || originalHeight > MAX_IMAGE_DIMENSION;
  const canResize = detectedFormat === "jpeg" || detectedFormat === "png" || detectedFormat === "webp";

  let processedBlob = sourceBlob;
  let processedWidth = originalWidth;
  let processedHeight = originalHeight;

  if (needsResize) {
    if (!canResize) {
      throw new Error(t`This image format cannot be resized in the browser. Please upload a smaller image.`);
    }
    processedBlob = await resizeRasterImage(sourceBlob, detectedMime, originalWidth, originalHeight);
    const processedDimensions = await decodeDimensions(processedBlob);
    processedWidth = processedDimensions.width;
    processedHeight = processedDimensions.height;
  }

  const fileName = `${createUuid32()}${extensionForFormat(detectedFormat)}`;
  const processedFile = new File([processedBlob], fileName, {
    type: detectedMime,
    lastModified: Date.now(),
  });

  return {
    file: processedFile,
    format: detectedFormat,
    originalName: rawFile.name,
    originalSizeBytes: rawFile.size,
    originalWidth,
    originalHeight,
    processedWidth,
    processedHeight,
    previewUrl: URL.createObjectURL(processedFile),
  };
}

export function FileUploader({
  module,
  label,
  optional = true,
  accept = "image/*",
  hint,
  maxFiles = DEFAULT_MAX_FILES,
  maxTotalSizeMb = DEFAULT_MAX_TOTAL_SIZE_MB,
  disabled = false,
  multiple = true,
  className,
  onUploadSuccess,
  onUploadedItemsChange,
  onUploadError,
  existingImage,
  onExistingImageDetachedChange,
}: FileUploaderProps) {
  const { t } = useLingui();
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadedItemsRef = useRef<UploadedFileItem[]>([]);
  const resolvedHint =
    hint ??
    `${SUPPORTED_IMAGE_TYPES} (${t`max ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION}px`})`;

  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadedItems, setUploadedItems] = useState<UploadedFileItem[]>([]);
  const [isExistingImageHidden, setIsExistingImageHidden] = useState(false);
  const [isExistingImagePreviewOpen, setIsExistingImagePreviewOpen] = useState(false);

  const existingImagePreviewLabel = existingImage?.label ?? t`Current thumbnail`;
  const existingThumbnailUrl = existingImage?.thumbnailUrl ?? existingImage?.imageUrl;
  const existingFullImageUrl = existingImage?.imageUrl ?? existingImage?.thumbnailUrl;
  const existingThumbnailFileName = existingThumbnailUrl ? getFileNameFromUrl(existingThumbnailUrl) : "";
  const hasExistingImage = Boolean(existingThumbnailUrl);

  useEffect(() => {
    uploadedItemsRef.current = uploadedItems;
  }, [uploadedItems]);

  useEffect(() => {
    return () => {
      uploadedItemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, []);

  function emitUploadedItems(nextItems: UploadedFileItem[]) {
    setUploadedItems(nextItems);
    onUploadedItemsChange?.(nextItems);
  }

  async function uploadFiles(incomingFiles: File[]) {
    if (!incomingFiles.length || disabled || isUploading) return;
    const hasVisibleExistingImage = hasExistingImage && !isExistingImageHidden;

    if (!multiple && incomingFiles.length > 1) {
      const error = new Error(t`Only one file can be uploaded.`);
      toast.warning(error.message);
      onUploadError?.(error);
      return;
    }

    if (!multiple && hasVisibleExistingImage) {
      const error = new Error(t`Only one file is allowed. Detach the current image before uploading a new one.`);
      toast.warning(error.message);
      onUploadError?.(error);
      return;
    }

    if (!multiple && uploadedItems.length >= 1) {
      const error = new Error(t`Only one file is allowed. Remove the current file before uploading a new one.`);
      toast.warning(error.message);
      onUploadError?.(error);
      return;
    }

    const maxTotalBytes = maxTotalSizeMb * 1024 * 1024;
    const currentSize = uploadedItems.reduce((sum, item) => sum + item.sizeBytes, 0);

    if (uploadedItems.length + incomingFiles.length > maxFiles) {
      const error = new Error(t`You can upload up to ${maxFiles} files.`);
      toast.error(error.message);
      onUploadError?.(error);
      return;
    }

    setIsUploading(true);

    const nextItems = [...uploadedItems];
    const successfulUploads: UploadImageResponse[] = [];
    let nextTotalSize = currentSize;
    const knownFileHashes = new Set(uploadedItems.map((item) => item.fileHash));

    try {
      for (const rawFile of incomingFiles) {
        const fileHash = await hashFile(rawFile);

        if (knownFileHashes.has(fileHash)) {
          throw new Error(t`File is already uploaded.`);
        }

        knownFileHashes.add(fileHash);

        const processed = await processImageFile(rawFile);

        if (nextTotalSize + processed.file.size > maxTotalBytes) {
          URL.revokeObjectURL(processed.previewUrl);
          throw new Error(t`Total file size cannot exceed ${maxTotalSizeMb} MB.`);
        }

        const uploaded = await uploadApi.uploadImage(module, processed.file, {
          originalFileName: processed.originalName,
          processedFileName: processed.file.name,
          originalSizeBytes: processed.originalSizeBytes,
          processedSizeBytes: processed.file.size,
          originalWidth: processed.originalWidth,
          originalHeight: processed.originalHeight,
          processedWidth: processed.processedWidth,
          processedHeight: processed.processedHeight,
        });

        nextTotalSize += processed.file.size;
        nextItems.push({
          id: uploaded.key,
          fileName: uploaded.fileName,
          key: uploaded.key,
          imageUrl: uploaded.imageUrl,
          thumbnailKey: uploaded.thumbnailKey,
          thumbnailUrl: uploaded.thumbnailUrl,
          fileHash,
          sizeBytes: processed.file.size,
          width: uploaded.width,
          height: uploaded.height,
          previewUrl: processed.previewUrl,
        });
        successfulUploads.push(uploaded);
      }

      toast.success(t`Image upload completed`);
      emitUploadedItems(nextItems);
      onUploadSuccess?.(successfulUploads);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(t`Failed to upload image`);
      toast.error(err.message);
      onUploadError?.(err);
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  }

  function removeItem(id: string) {
    const target = uploadedItems.find((item) => item.id === id);
    if (target) {
      URL.revokeObjectURL(target.previewUrl);
    }
    const next = uploadedItems.filter((item) => item.id !== id);
    emitUploadedItems(next);
  }

  function setDefaultUploadedItem(id: string) {
    const index = uploadedItems.findIndex((item) => item.id === id);
    if (index <= 0) return;

    const next = [...uploadedItems];
    const [selected] = next.splice(index, 1);
    if (!selected) return;
    next.unshift(selected);
    emitUploadedItems(next);
  }

  function detachExistingImage() {
    setIsExistingImageHidden(true);
    setIsExistingImagePreviewOpen(false);
    onExistingImageDetachedChange?.(true);
  }

  function undoDetachExistingImage() {
    setIsExistingImageHidden(false);
    onExistingImageDetachedChange?.(false);
  }

  function onInputChange(fileList: FileList | null) {
    if (!fileList?.length || disabled || isUploading) return;
    void uploadFiles(Array.from(fileList));
  }

  function onDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setIsDragActive(false);

    if (disabled || isUploading || !event.dataTransfer.files?.length) return;
    void uploadFiles(Array.from(event.dataTransfer.files));
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {label && (
        <p className="text-sm font-medium text-(--color-text-primary)">
          {label}
          {optional && (
            <span className="ml-1 font-normal text-(--color-text-secondary)">{t`(optional)`}</span>
          )}
        </p>
      )}

      {hasExistingImage && !isExistingImageHidden && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-(--color-text-primary)">{t`Uploaded image`}</p>
          <div className="flex">
            <div
              role="button"
              tabIndex={0}
              onClick={() => existingFullImageUrl && setIsExistingImagePreviewOpen(true)}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if ((event.key === "Enter" || event.key === " ") && existingFullImageUrl) {
                  event.preventDefault();
                  setIsExistingImagePreviewOpen(true);
                }
              }}
              className="relative flex items-center gap-2 rounded-[10px] border border-(--color-border) bg-(--color-surface-content) p-2 text-left hover:bg-(--color-surface-muted)"
            >
              <img
                src={existingThumbnailUrl}
                alt={existingImagePreviewLabel}
                className="size-10 shrink-0 rounded-[8px] object-cover"
              />
              <div className="min-w-0 flex-1 pr-6">
                <p className="truncate text-xs font-medium text-(--color-text-primary)">{existingImagePreviewLabel}</p>
                <p className="truncate text-[11px] text-(--color-text-secondary)">{existingThumbnailFileName}</p>
              </div>
              {uploadedItems.length === 0 && (
                <span className="rounded-full bg-(--color-surface-muted) px-2 py-0.5 text-[10px] font-medium text-(--color-text-secondary)">
                  {t`Default`}
                </span>
              )}
              <span className="absolute right-1 top-1 rounded-full bg-(--color-surface-content)">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    detachExistingImage();
                  }}
                  className="rounded-full p-1 text-(--color-text-secondary) transition-colors hover:bg-(--color-surface-muted) hover:text-(--color-text-primary)"
                  aria-label={t`Detach image`}
                >
                  <X className="size-4" />
                </button>
              </span>
            </div>
          </div>
        </div>
      )}

      {hasExistingImage && isExistingImageHidden && uploadedItems.length === 0 && (
        <div className="flex items-center justify-between rounded-[10px] border border-dashed border-(--color-border) bg-(--color-surface-content) px-3 py-2">
          <p className="text-xs text-(--color-text-secondary)">{t`Existing image will be detached when you save.`}</p>
          <button
            type="button"
            onClick={undoDetachExistingImage}
            className="text-xs font-medium text-(--color-action) hover:underline"
          >
            {t`Undo`}
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled && !isUploading) {
            setIsDragActive(true);
          }
        }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={onDrop}
        disabled={disabled || isUploading}
        className={cn(
          "flex min-h-[128px] w-full flex-col items-center justify-center gap-2 rounded-[14px] border bg-(--color-surface-content) px-5 py-4 text-center transition-colors",
          isDragActive
            ? "border-(--brand-black) bg-(--color-surface-muted)"
            : "border-(--color-border) hover:bg-(--color-surface-muted)",
          (disabled || isUploading) && "cursor-not-allowed opacity-70 hover:bg-(--color-surface-content)",
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={(event) => onInputChange(event.target.files)}
        />

        <div className="flex size-12 items-center justify-center rounded-full border-[8px] border-(--color-border) bg-(--color-surface-accent)">
          {isUploading ? (
            <Loader2 className="size-5 animate-spin text-(--color-text-secondary)" />
          ) : (
            <UploadCloud className="size-5 text-(--color-text-secondary)" />
          )}
        </div>

        <div className="text-[18px] leading-7 text-(--color-text-secondary)">
          <span className="font-semibold">
            {isUploading ? t`Uploading...` : t`Click to upload`}
          </span>
          {!isUploading && <span className="text-(--color-text-secondary)"> {t`or drag and drop`}</span>}
        </div>

        <p className="text-sm text-(--color-text-secondary)">
          {resolvedHint}
        </p>
      </button>

      {uploadedItems.length > 0 && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {uploadedItems.map((item, index) => (
            <div
              key={item.id}
              className="flex items-center gap-2 rounded-[10px] border border-(--color-border) bg-(--color-surface-content) p-2"
            >
              <img
                src={item.previewUrl}
                alt={item.fileName}
                className="size-12 rounded-[8px] object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-(--color-text-primary)">{item.fileName}</p>
                {index === 0 ? (
                  <span className="inline-flex rounded-full bg-(--color-surface-muted) px-2 py-0.5 text-[10px] font-medium text-(--color-text-secondary)">
                    {t`Default`}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setDefaultUploadedItem(item.id)}
                    className="text-[11px] font-medium text-(--color-action) hover:underline"
                  >
                    {t`Set default`}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                className="rounded-full p-1 text-(--color-text-secondary) transition-colors hover:bg-(--color-surface-muted) hover:text-(--color-text-primary)"
                aria-label={t`Remove file`}
              >
                <X className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isExistingImagePreviewOpen} onOpenChange={setIsExistingImagePreviewOpen}>
        {/* Opens on top of the dialog that hosts the uploader, hence z-[70]. */}
        <DialogContent
          hideClose
          overlayClassName="z-[70] bg-black/70"
          className="z-[70] w-[min(90vw,51.25rem)] max-w-none max-h-[90vh] gap-0 overflow-hidden rounded-xl border-0 bg-(--color-surface-content) p-0 shadow-xl"
        >
          <DialogTitle className="sr-only">{t`Image preview`}</DialogTitle>
          <DialogDescription className="sr-only">
            {t`Full-size image preview for the selected file.`}
          </DialogDescription>
          <div className="flex items-center justify-between border-b border-(--color-border) px-4 py-3">
            <p className="text-sm font-medium text-(--color-text-primary)">{t`Image preview`}</p>
            <button
              type="button"
              onClick={() => setIsExistingImagePreviewOpen(false)}
              className="rounded-full p-1 text-(--color-text-secondary) transition-colors hover:bg-(--color-surface-muted) hover:text-(--color-text-primary)"
              aria-label={t`Close`}
            >
              <X className="size-4" />
            </button>
          </div>
          <div className="flex max-h-[calc(90vh-3.25rem)] items-center justify-center p-4">
            {existingFullImageUrl ? (
              <img src={existingFullImageUrl} alt={t`Image preview`} className="max-h-full max-w-full rounded-lg object-contain" />
            ) : (
              <p className="text-sm text-(--color-text-secondary)">{t`No image available`}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
